import React from 'react';

interface ProcessStateConfig {
  label: string;
  color: string;
  bgColor: string;
  icon: string;
}

interface ProcessStateIndicatorProps {
  /** Current state value */
  state: string;
  /** Optional custom state configuration */
  stateConfig?: Record<string, ProcessStateConfig>;
  /** Label above the state (default: 'Process State') */
  label?: string;
}

/**
 * Default state configurations for mixer processes
 */
const DEFAULT_STATE_CONFIG: Record<string, ProcessStateConfig> = {
  'Idle': {
    label: 'IDLE',
    color: '#6c757d',
    bgColor: 'rgba(108, 117, 125, 0.1)',
    icon: '⏸️'
  },
  'Phase1': {
    label: 'PHASE 1',
    color: '#53bde4',
    bgColor: 'rgba(83, 189, 228, 0.1)',
    icon: '🔵'
  },
  'Phase2': {
    label: 'PHASE 2',
    color: '#00ff88',
    bgColor: 'rgba(0, 255, 136, 0.1)',
    icon: '🟢'
  },
  'Phase3': {
    label: 'PHASE 3',
    color: '#ffc107',
    bgColor: 'rgba(255, 193, 7, 0.1)',
    icon: '🟡'
  },
  'End': {
    label: 'END',
    color: '#dc3545',
    bgColor: 'rgba(220, 53, 69, 0.1)',
    icon: '🔴'
  },
  'FERMENTING': {
    label: 'FERMENTING',
    color: '#9c27b0',
    bgColor: 'rgba(156, 39, 176, 0.1)',
    icon: '🧬'
  },
};

/**
 * Displays the current process state with colored badge styling
 */
const ProcessStateIndicator: React.FC<ProcessStateIndicatorProps> = ({
  state,
  stateConfig = DEFAULT_STATE_CONFIG,
  label = 'Process State',
}) => {
  const currentState = stateConfig[state] || stateConfig['Idle'] || {
    label: state.toUpperCase(),
    color: '#6c757d',
    bgColor: 'rgba(108, 117, 125, 0.1)',
    icon: '🔄'
  };

  return (
    <div style={{
      background: currentState.bgColor,
      border: `2px solid ${currentState.color}`,
      borderRadius: '8px',
      padding: '0.75rem 1.25rem',
      minWidth: '140px',
      boxShadow: `0 0 20px ${currentState.color}40`,
    }}>
      <div style={{
        fontSize: '0.65rem',
        color: currentState.color,
        textTransform: 'uppercase',
        letterSpacing: '1px',
        marginBottom: '6px',
        fontWeight: 'bold',
      }}>
        {label}
      </div>
      <div style={{
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: currentState.color,
        fontFamily: 'monospace',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}>
        <span>{currentState.icon}</span>
        <span>{currentState.label}</span>
      </div>
    </div>
  );
};

export default ProcessStateIndicator;
export { DEFAULT_STATE_CONFIG };
export type { ProcessStateConfig, ProcessStateIndicatorProps };
